/**
 * Live floor-plan documents, one per board node:
 * `portalProjectBoards/{dealId}/floorPlans/{nodeId}`.
 *
 * Writes use a transaction + 3-way merge so two people can add furniture
 * without last-write-wins wiping the other person's items.
 */
import { db } from './firebase';
import {
  collection,
  doc,
  onSnapshot,
  runTransaction,
  Timestamp,
} from 'firebase/firestore';
import type { BoardFloorPlanData } from '../types';
import { clonePlan } from '../components/board/floorplan/floorPlanMath';
import {
  dirtyEntityIds,
  threeWayMergeFloorPlan,
  type FloorPlanOmittedIds,
} from '../components/board/floorplan/floorPlanMerge';

const COLLECTION = 'portalProjectBoards';

function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefined(v)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      if (v !== undefined) out[k] = stripUndefined(v as unknown);
    });
    return out as T;
  }
  return value;
}

function isFloorPlanData(value: unknown): value is BoardFloorPlanData {
  if (!value || typeof value !== 'object') return false;
  const data = value as BoardFloorPlanData;
  return !!data.footprint && Array.isArray(data.walls) && Array.isArray(data.items);
}

export function floorPlanLiveRef(dealId: string, nodeId: string) {
  return doc(db, COLLECTION, dealId, 'floorPlans', nodeId);
}

export type FloorPlanLiveRevMap = Record<string, number>;

export function subscribeFloorPlans(
  dealId: string,
  callback: (plans: Record<string, BoardFloorPlanData>, revs: FloorPlanLiveRevMap) => void,
): () => void {
  return onSnapshot(
    collection(db, COLLECTION, dealId, 'floorPlans'),
    (snap) => {
      const plans: Record<string, BoardFloorPlanData> = {};
      const revs: FloorPlanLiveRevMap = {};
      snap.docs.forEach((entry) => {
        const raw = entry.data()?.data;
        if (!isFloorPlanData(raw)) return;
        plans[entry.id] = raw;
        revs[entry.id] = Number(entry.data()?.rev || 0);
      });
      callback(plans, revs);
    },
    (err) => {
      console.warn('[floorPlanLiveService] subscribe failed', err);
      callback({}, {});
    },
  );
}

export async function saveFloorPlanLive(
  dealId: string,
  nodeId: string,
  local: BoardFloorPlanData,
  base: BoardFloorPlanData,
  updatedBy?: string,
  omitted?: FloorPlanOmittedIds,
  dirty?: FloorPlanOmittedIds,
): Promise<{ data: BoardFloorPlanData; rev: number }> {
  const ref = floorPlanLiveRef(dealId, nodeId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const remoteRaw = snap.exists() ? snap.data()?.data : null;
    const merged = isFloorPlanData(remoteRaw)
      ? threeWayMergeFloorPlan(base, local, remoteRaw, omitted, { ...dirtyEntityIds(base, local), ...dirty })
      : clonePlan(local);
    const rev = Number(snap.exists() ? snap.data()?.rev || 0 : 0) + 1;
    tx.set(
      ref,
      stripUndefined({
        data: merged,
        rev,
        updatedAt: Timestamp.now(),
        updatedBy: updatedBy || 'unknown',
      }),
      { merge: true },
    );
    return { data: merged, rev };
  });
}
